import { z } from 'zod';
import { MoneyCentsSchema } from './money.js';
import { PaymentMethodSchema } from './enums.js';
export const TodayReportTotalsSchema = z.object({
    saleCount: z.number().int(),
    itemCount: z.number().int(),
    subtotalCents: MoneyCentsSchema,
    /** The MANUAL discount total only. See `autoDiscountCents` for the automatic one. */
    discountCents: MoneyCentsSchema,
    autoDiscountCents: MoneyCentsSchema,
    totalCents: MoneyCentsSchema,
    voidedCount: z.number().int(),
});
export const TodayReportPaymentBreakdownSchema = z.object({
    method: PaymentMethodSchema,
    saleCount: z.number().int(),
    totalCents: MoneyCentsSchema,
});
export const TodayReportTopItemSchema = z.object({
    productId: z.number().int(),
    productName: z.string(),
    qty: z.number().int(),
    totalCents: MoneyCentsSchema,
});
export const TodayReportUserBreakdownSchema = z.object({
    userId: z.number().int(),
    /** Snapshot uuid/code/name (see docs/DECISIONS.md) - grouping is by `uuid` + `displayName` together,
     * so a handover that happened partway through the day splits correctly into two rows. */
    uuid: z.string(),
    shortCode: z.string(),
    displayName: z.string(),
    saleCount: z.number().int(),
    totalCents: MoneyCentsSchema,
});
/** Admin-only, included in `TodayReportSchema.lowStock` for the dashboard. */
export const LowStockItemSchema = z.object({
    productId: z.number().int(),
    name: z.string(),
    code: z.string(),
    stockQty: z.number().int(),
    lowStockThreshold: z.number().int(),
});
export const TodayReportSchema = z.object({
    date: z.string(),
    scope: z.enum(['own', 'all']),
    totals: TodayReportTotalsSchema,
    byPaymentMethod: z.array(TodayReportPaymentBreakdownSchema),
    topItems: z.array(TodayReportTopItemSchema),
    byUser: z.array(TodayReportUserBreakdownSchema).optional(),
    /** Admin only: active products at or below their low-stock threshold. */
    lowStock: z.array(LowStockItemSchema).optional(),
});
// ---------------------------------------------------------------------------
// GET /api/reports/today/breakdown - admin-only, feeds the dashboard's
// "Today's sales" bar/pie charts.
// ---------------------------------------------------------------------------
export const TodayBreakdownGroupBySchema = z.enum(['category', 'product']);
/** One group's quantity within a single hour. `key` is stable (a category id,
 * `"none"` for uncategorised, or a product id, all as strings) for colour
 * assignment on the client; `label` is the display name. */
export const TodayBreakdownSegmentSchema = z.object({
    key: z.string(),
    label: z.string(),
    qty: z.number().int(),
});
export const TodayBreakdownHourSchema = z.object({
    /** 0-23, KL local hour. */
    hour: z.number().int().min(0).max(23),
    total: z.number().int(),
    segments: z.array(TodayBreakdownSegmentSchema),
});
export const TodayBreakdownTotalSchema = z.object({
    key: z.string(),
    label: z.string(),
    qty: z.number().int(),
    /** Gross, pre-discount - the sum of this group's `sale_items.line_total_cents`,
     * not the net amount actually charged (discounts are sale-level, not
     * per-item). Summed across every group this equals the Today report's
     * `totals.subtotalCents` for the same day. */
    revenueCents: MoneyCentsSchema,
});
export const TodayBreakdownResponseSchema = z.object({
    /** The resolved KL calendar date this breakdown covers - today's, or the requested `?date=`. */
    date: z.string(),
    groupBy: TodayBreakdownGroupBySchema,
    /** Hours from the first sale of the day through the current hour,
     * inclusive, with no gaps. Empty when there are no non-voided sales today. */
    hours: z.array(TodayBreakdownHourSchema),
    /** Every group that sold at least one unit today, largest qty first. */
    totals: z.array(TodayBreakdownTotalSchema),
    totalQty: z.number().int(),
});
//# sourceMappingURL=reports.js.map